/* eslint-disable webflow/module-top-level-imports */

// HACK: Manually tree-shake here to limit dependency tree size
import * as Plugins from '@packages/systems/wfdl/Plugins';
import * as Component from '@packages/systems/wfdl/Component';
import * as Expressions from '@packages/systems/wfdl/Expressions';
import * as Variable from '@packages/systems/wfdl/Variable';
import {matchExpression} from '@packages/systems/wfdl/matchExpression';
import {matchType} from '@packages/systems/wfdl/matchType';

/* eslint-enable webflow/module-top-level-imports */

import {
  namesEqual,
  qualify,
  type Qualified,
  toHumanReadableString,
  typeName,
  identifier,
} from '@packages/systems/wfdl-Qualified';
import {
  EElement,
  EText,
  ERecord,
  ESelect,
  EBoolean,
  ENumber,
  EEnum,
  EUnion,
  ECase,
  EList,
  EVariable,
  ECall,
  EFunction,
  ELiteral,
  EHole,
  EDistinct,
  TGeneric,
  TRowCons,
  TTypeApplication,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {type Type} from '@packages/systems/wfdl';
import {pair, getSecond} from '@packages/utilities/Pair';
import {parseType} from './parseType';

/**
 * wfdl`` is a template literal parser for WFDL syntax.
 * See: https://platform.wfdev.io/guides/wfdl-syntax
 */
export function wfdl(
  input: TemplateStringsArray,
  ...exprs: Expression[]
): Expression {
  const tree = parse(
    input.reduce((memo, src, index) => {
      const isEndOfInterpolations = index === input.length - 1;
      return `${memo}${src}${
        isEndOfInterpolations
          ? ''
          : getSeparator(src) + getPlaceholderIdentifierAtIndex(index)
      }`;
    }, '')
  );
  return getSecond(
    // @ts-expect-error - TS2345 - Argument of type '(expr: ExpressionF<Expression>) => (state: { cursor: number; exprs: Expression[]; }) => Pair<{ cursor: number; exprs: Expression[]; }, Expression | undefined>' is not assignable to parameter of type 'UpdaterFnType<{ cursor: number; exprs: Expression[]; }, Expression>'.
    Expressions.everywhereWithState(getPlaceholderReplacer)(tree)({
      exprs,
      cursor: 0,
    })
  );
}

/**
 * wfdlType`` is a template literal parser for WFDL type syntax.
 *
 * This implementation is based heavily on Merrick's excellent wfdl template literal parser.
 * The way we allow you to inject types as ${param}s is by replacing each one with a uniquely-named TypeReference string
 * before parsing (see input.reduce inside the call of parseType),
 * then swapping those concrete type values back in after parsing.
 */
export const wfdlType = (
  input: ReadonlyArray<string>,
  ...types: Type[]
): Type => {
  const typeTree = parseType(
    input.reduce((memo, src, index) => {
      const isEndOfInterpolations = index === input.length - 1;
      return `${memo}${src}${
        isEndOfInterpolations
          ? ''
          : getSeparator(src) + getPlaceholderIdentifierAtIndex(index)
      }`;
    }, '')
  );

  let cursor = 0;
  return mapType(
    // @ts-expect-error - TS2345 - Argument of type '(dataType: Type) => Type | undefined' is not assignable to parameter of type '(arg1: Type) => Type'.
    matchType({
      TypeReference: (ref, ty) => {
        if (ref === getPlaceholderIdentifierAtIndex(cursor)) {
          const result = types[cursor];
          cursor += 1;
          return result;
        }
        return ty;
      },
      _: (ty) => ty,
    })
  )(typeTree);
};

// Regex characters
const whitespace = /\s/;

function isDigit(code: number): boolean {
  return code > 47 && code < 58;
}

function isAlphaNumeric(code: number): boolean {
  if (
    !(code > 47 && code < 58) && // numeric
    !(code > 64 && code < 91) && // upper alpha
    !(code > 96 && code < 123) // lower alpha
  ) {
    return false;
  }
  return true;
}
function isIdentifierChar(code: number): boolean {
  if (
    !(code > 47 && code < 58) && // numeric
    !(code > 64 && code < 91) && // upper alpha
    !(code > 96 && code < 123) && // lower alpha
    code !== 95 && // '_'
    code !== 36 // '$'
  ) {
    return false;
  }
  return true;
}

function tokenizer(input: string): Array<Token> {
  let i = 0;
  /** Used for tracking where a token started within the string */
  let start = i;
  const tokens: Array<Token> = [];

  while (i < input.length) {
    let char = input[i];

    // Trim leading space
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    if (whitespace.test(char)) {
      i++;
      continue;
    }

    start = i;

    // Comment — skip from // to end of line
    if (char === '/' && input[i + 1] === '/') {
      while (i < input.length && input[i] !== '\n') {
        i++;
      }
      continue;
    }

    // String. Scan to the matching closing `"`, skipping past `\X` pairs
    // so that `\"` and `\\` don't fool the closer check, then unescape the
    // entire literal via `JSON.parse`. This matches the printer's use of
    // `JSON.stringify` in wfdl/codegen/wfdl.ts and supports the full set
    // of JSON escapes (\", \\, \/, \n, \r, \t, \b, \f, \uXXXX) — required
    // for round-tripping strings that contain `"`, `\`, or newlines (e.g.
    // an HtmlEmbed `html` field with inline SVG/HTML).
    if (char === '"') {
      let j = i + 1;
      while (j < input.length) {
        const c = input[j];
        if (c === '"') break;
        if (c === '\n') {
          throw new WFDLSyntaxError('Unterminated string literal', input, {
            start,
            length: j - start,
          });
        }
        // Skip past escape sequences so `\"` doesn't end the string.
        // JSON.parse below validates the escape body.
        j += c === '\\' ? 2 : 1;
      }
      if (j >= input.length) {
        throw new WFDLSyntaxError('Unterminated string literal', input, {
          start,
          length: j - start,
        });
      }
      const raw = input.substring(i, j + 1);
      let val: string;
      try {
        val = JSON.parse(raw);
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e);
        throw new WFDLSyntaxError(`Invalid string literal: ${message}`, input, {
          start,
          length: raw.length,
        });
      }
      i = j + 1;
      tokens.push({type: 'string', val, loc: {start, length: i - start}});
      continue;
    }

    // Number (including negative numbers)
    if (
      char &&
      (isDigit(char.charCodeAt(0)) ||
        (char === '-' &&
          i + 1 < input.length &&
          isDigit(input.charCodeAt(i + 1))))
    ) {
      let j = i;
      // If it starts with '-', include it in the number
      if (char === '-') {
        char = input[++j];
      }
      while (
        char &&
        (isAlphaNumeric(char.charCodeAt(0)) || char === '.') &&
        j < input.length
      ) {
        char = input[++j];
      }
      const val = input.substring(i, j);
      i = j;
      tokens.push({type: 'number', val, loc: {start, length: i - start}});
      continue;
    }

    // Greater than
    if (char === '>') {
      i++;
      tokens.push({type: 'gt', loc: {start, length: 1}});
      continue;
    }
    // Boolean true
    if (input.substring(i, i + 2) === '</') {
      i += 2;
      tokens.push({type: 'lt-end', loc: {start, length: 2}});
      continue;
    }
    // Less than
    if (char === '<') {
      i++;
      tokens.push({type: 'lt', loc: {start, length: 1}});
      continue;
    }

    // Boolean true
    if (input.substring(i, i + 4) === 'true') {
      i += 4;
      tokens.push({type: 'true', loc: {start, length: 4}});
      continue;
    }

    // Boolean false
    if (input.substring(i, i + 5) === 'false') {
      i += 5;
      tokens.push({type: 'false', loc: {start, length: 4}});
      continue;
    }

    // Case
    if (input.substring(i, i + 4) === 'case') {
      i += 4;
      tokens.push({type: 'case', loc: {start, length: 4}});
      continue;
    }

    // Literal
    if (input.substring(i, i + 7) === 'literal') {
      i += 7;
      tokens.push({type: 'literal', loc: {start, length: 7}});
      continue;
    }

    // Distinct
    if (input.substring(i, i + 8) === 'distinct') {
      i += 8;
      tokens.push({type: 'distinct', loc: {start, length: 8}});
      continue;
    }

    // Qualifier
    if (input.substring(i, i + 2) === '::') {
      i += 2;
      tokens.push({type: 'qualifier', loc: {start, length: 2}});
      continue;
    }

    // Identifier
    if (char && isIdentifierChar(char.charCodeAt(0))) {
      let val = '';
      while (char && isIdentifierChar(char.charCodeAt(0)) && i < input.length) {
        val += char;
        char = input[++i];
      }
      tokens.push({type: 'identifier', val, loc: {start, length: i - start}});
      continue;
    }

    // Hole
    if (char === '-') {
      i++;
      tokens.push({type: 'hole', loc: {start, length: 1}});
      continue;
    }

    // Union
    if (char === '^') {
      i++;
      tokens.push({type: 'union', loc: {start, length: 1}});
      continue;
    }

    // Accessor
    if (char === '.') {
      i++;
      tokens.push({type: 'accessor', loc: {start, length: 1}});
      continue;
    }

    // Separators
    if (char === ',') {
      i++;
      tokens.push({type: 'next', loc: {start, length: 1}});
      continue;
    }
    if (char === ':') {
      i++;
      tokens.push({type: 'pair', loc: {start, length: 1}});
      continue;
    }

    // Function definition
    if (input.substring(i, i + 2) === '=>') {
      i += 2;
      tokens.push({type: 'function', loc: {start, length: 1}});
      continue;
    }

    // Paren
    if (char === '(') {
      char = input[++i];
      tokens.push({type: 'left-paren', loc: {start, length: 1}});
      start = i;
      // Edge case literal value check after opening parenthesis
      // Tokens array should have [literal, identifier, accessor, identifier, left-paren]
      // -> tokens[tokens.length - 5].type === 'literal' => true
      if (
        tokens[tokens.length - 5] &&
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        tokens[tokens.length - 5].type === 'literal'
      ) {
        let parenCount = 1;
        let val = '';
        while (parenCount > 0) {
          val += char;
          char = input[++i];
          if (char === '(') {
            parenCount++;
          } else if (char === ')') {
            parenCount--;
          }
        }
        tokens.push({type: 'json', val, loc: {start, length: i - start}});
      }
      continue;
    }
    if (char === ')') {
      i++;
      tokens.push({type: 'right-paren', loc: {start, length: 1}});
      continue;
    }

    // Curly
    if (char === '{') {
      i++;
      tokens.push({type: 'left-curly', loc: {start, length: 1}});
      continue;
    }
    // Curly
    if (char === '}') {
      i++;
      tokens.push({type: 'right-curly', loc: {start, length: 1}});
      continue;
    }

    // Bracket
    if (char === '[') {
      i++;
      tokens.push({type: 'left-bracket', loc: {start, length: 1}});
      continue;
    }
    // Bracket
    if (char === ']') {
      i++;
      tokens.push({type: 'right-bracket', loc: {start, length: 1}});
      continue;
    }

    throw new WFDLSyntaxError(`Unexpected character: ${char}`, input, {
      start,
      length: 1,
    });
  }

  return tokens;
}

/**
 * `parse` a `wfdl` text string into an abstract syntax tree
 */
export function parse(input: string): Expression {
  const tokens = tokenizer(input);
  return parser(tokens, input);
}

function parser(tokens: Array<Token>, input: string): Expression {
  const inst = new Parser(tokens, input);
  inst.go();
  return inst.popExprGuarantee();
}

/**
 * When we have two interpolations side by side ${a}${b} we don't want to
 * generate a composite identifier 'ab' we want 'a b'.
 */
const getSeparator = (separator: string) => (separator === '' ? ' ' : '');

/**
 * Generate a temporary identifier which is used internally with the `wfdl`
 * template function. These identifiers are temporary because they ultimately
 * get replaced with the expressions within the template string `${ ... }`.
 *
 * You probably don't need to use this function. It's really only useful for
 * tooling that deals with source locations within generated `wfdl` template
 * strings, for example if there's a syntax error whose source-location you'll
 * need to map back to the original template string segments.
 */
export const getPlaceholderIdentifierAtIndex = (index: number) =>
  `_wfdl_template_placeholder_${index}`;

const getPlaceholderReplacer = matchExpression({
  Variable: (ident) => (state: {cursor: number; exprs: Array<Expression>}) => {
    return ident === getPlaceholderIdentifierAtIndex(state.cursor)
      ? pair(
          {
            ...state,
            cursor: state.cursor + 1,
          },
          state.exprs[state.cursor]
        )
      : pair(state, EVariable(ident));
  },

  _: (expr) => (state: {cursor: number; exprs: Array<Expression>}) =>
    pair(state, expr),
});

/** Walks a tree of types and calls a mapper function for every leaf type */
const mapType = (mapper: (arg1: Type) => Type): ((dataType: Type) => Type) =>
  matchType<Type>({
    Element: (_, ty) => mapper(ty),

    Generic: ({arg, body}) => TGeneric({arg, body: mapType(mapper)(body)}),
    Rigid: (_, ty) => mapper(ty),
    RowCons: ({label, tail, type}) =>
      TRowCons({
        label,
        // Order is important here:
        type: mapType(mapper)(type),
        tail: mapType(mapper)(tail),
      }),
    RowEmpty: (_, ty) => mapper(ty),
    TypeApplication: ({arg, con}) =>
      TTypeApplication({
        // Order is important here:
        con: mapType(mapper)(con),
        arg: mapType(mapper)(arg),
      }),
    TypeConstructor: (_, ty) => mapper(ty),
    TypeReference: (_, ty) => mapper(ty),
    Unknown: (_, ty) => mapper(ty),
  });

/**
 * `parseWithSourceMap` a `wfdl` text string into an abstract syntax tree with
 * source map
 */
export function parseWithSourceMap(input: string): [Expression, SourceMap] {
  const tokens = tokenizer(input);
  const inst = new Parser(tokens, input);
  inst.go();
  return [inst.popExprGuarantee(), inst.sourceMap];
}

export class SourceMap {
  input: string;
  tokens: Array<Token>;
  expressionTokenRanges: Map<Expression, [number, number]>;

  constructor(input: string, tokens: Array<Token>) {
    this.input = input;
    this.tokens = tokens;
    this.expressionTokenRanges = new Map();
  }

  add(expr: Expression, tokensStart: number, tokensEnd: number): SourceMap {
    this.expressionTokenRanges.set(expr, [tokensStart, tokensEnd]);
    return this;
  }

  getHumanReadableSourceLocation(
    expr: Expression
  ): null | HumanReadableSourceLocation {
    const tokensRange = this.expressionTokenRanges.get(expr);
    if (tokensRange == null) return null;

    const [tokensStart, tokensEnd] = tokensRange;
    const firstToken = this.tokens[tokensStart];
    if (firstToken == null) return null;

    const lastToken = this.tokens[Math.max(tokensStart, tokensEnd)];
    if (lastToken == null) return null;

    const {start} = firstToken.loc;
    return getHumanReadableSourceLocation(this.input, {
      start,
      length: lastToken.loc.start + lastToken.loc.length - start,
    });
  }
}

export class Parser {
  /** Used for error reporting */
  input: string;

  cursor: number;
  token: Token;
  tokens: Array<Token>;
  exprStack: Array<Expression>;
  sourceMap: SourceMap;

  constructor(tokens: Array<Token>, input: string) {
    this.cursor = 0;
    const firstToken = tokens[0];
    if (!firstToken) {
      throw new WFDLSyntaxError('Unexpected empty token array', input, {
        start: input.length - 1,
        length: 1,
      });
    }
    this.token = firstToken;
    this.tokens = tokens;
    this.exprStack = [];
    this.input = input;
    this.sourceMap = new SourceMap(input, tokens);
  }

  nextToken = () => {
    this.cursor += 1;
    const maybeNextToken = this.tokens[this.cursor];
    if (!maybeNextToken) {
      throw new WFDLSyntaxError('Unexpected end of input', this.input, {
        start: this.input.length - 1,
        length: 1,
      });
    }
    this.token = maybeNextToken;
    // Sometimes we want to use the token returned from
    // here to tell TS to ignore previous assertions it has made about
    // the token
    return this.token;
  };

  currentToken = () => {
    // Sometimes we want to use the token returned from
    // here to tell TS to ignore previous assertions it has made about
    // the token
    return this.token;
  };

  pushExpr = (expr: Expression, tokensStart: number) => {
    // Store the range of tokens we consumed while producing this expression:
    this.sourceMap.add(expr, tokensStart, this.cursor);
    return this.exprStack.push(expr);
  };

  popExpr = (): Expression | undefined => {
    return this.exprStack.pop();
  };

  popExprGuarantee = (): Expression => {
    const poppedExpr = this.exprStack.pop();
    if (poppedExpr === undefined) {
      throw new WFDLSyntaxError(
        `Unexpected no Expression parsed`,
        this.input,
        this.token.loc
      );
    }
    return poppedExpr;
  };

  checkTokenType = (type: string) => {
    if (!this.token) {
      throw new WFDLSyntaxError('Unexpected end of input', this.input, {
        start: this.input.length - 1,
        length: 1,
      });
    }
    return this.token.type === type;
  };

  checkNextTokenType = (type: string) => {
    const nextToken = this.tokens[this.cursor + 1];
    return nextToken && nextToken.type === type;
  };

  confirmRequiredTokenType = (type: string) => {
    if (!this.checkTokenType(type)) {
      throw new WFDLSyntaxError(
        `Unexpected token type: ${this.token.type}, expected: ${type}`,
        this.input,
        this.token.loc
      );
    }
  };

  createObject = () => {
    const fields: Record<string, Expression> = {};
    let traversing = true;
    while (traversing) {
      // Check if we are at the end of the record
      if (this.token.type === 'right-curly') {
        traversing = false;
        continue;
      }

      this.addNextKeyValuePair(fields);

      const currentToken = this.currentToken();
      // Check if we have a next token, indicating a possibility for more fields
      if (currentToken.type === 'next') {
        this.nextToken();
        continue;
      } else if (currentToken.type === 'right-curly') {
        traversing = false;
        continue;
      }
      throw new WFDLSyntaxError(
        `Unexpected token type: ${currentToken.type}, expected: 'next' or 'right-curly'`,
        this.input,
        currentToken.loc
      );
    }
    return fields;
  };

  addNextKeyValuePair = (fields: Record<string, Expression>) => {
    // Confirm we first have an identifier
    if (this.token.type !== 'identifier' && this.token.type !== 'string') {
      throw new WFDLSyntaxError(
        `Unexpected token type: ${this.token.type}, expected: identifier or string`,
        this.input,
        this.token.loc
      );
    }
    const key = this.token.val;
    // Check for the ':', also called a pair token here
    this.nextToken();
    this.confirmRequiredTokenType('pair');
    this.nextToken();
    this.go();
    fields[key] = this.popExprGuarantee();
    this.nextToken();
  };

  getIdentifierString = (): string => {
    let result;

    if (this.token.type === 'identifier') {
      result = this.token.val;
    } else if (this.token.type === 'left-bracket') {
      // If we encounter a bracket, we're using bracket notation, so we
      // expect our next token to be a string:
      const nextToken = this.nextToken();
      if (nextToken.type !== 'string') {
        throw new WFDLSyntaxError(
          `Unexpected token type: ${nextToken.type}, expected: string`,
          this.input,
          nextToken.loc
        );
      }

      // This string is our identifier.
      result = nextToken.val;

      // Finally we expect our identifier string to be followed by a closing
      // bracket:
      const nextToken2 = this.nextToken();
      if (nextToken2.type !== 'right-bracket') {
        throw new WFDLSyntaxError(
          `Unexpected token type: ${nextToken2.type}, expected: right-bracket`,
          this.input,
          nextToken2.loc
        );
      }
    } else {
      throw new WFDLSyntaxError(
        `Unexpected token type: ${this.token.type}, expected: identifier`,
        this.input,
        this.token.loc
      );
    }

    return result;
  };

  getName = <Name>(fn: (arg1: string) => Name): Qualified<Name> => {
    // Confirm we first have an identifier

    if (this.token.type === 'qualifier') {
      this.nextToken();
    }

    const _pluginName = this.getIdentifierString();

    // Confirm we then have an accessor
    this.nextToken();
    this.confirmRequiredTokenType('qualifier');
    // Confirm we then have an identifier
    this.nextToken();

    const val = this.getIdentifierString();

    const name = qualify(Plugins.name(_pluginName), fn(val));
    this.nextToken();
    return name;
  };

  go = () => {
    if (!this.token) {
      throw new WFDLSyntaxError('Unexpected end of input', this.input, {
        start: this.input.length - 1,
        length: 1,
      });
    }

    const tokensStart = this.cursor;
    switch (this.token.type) {
      // EHole
      case 'hole': {
        this.pushExpr(EHole(), tokensStart);
        break;
      }
      // EText
      case 'string': {
        this.pushExpr(EText(this.token.val), tokensStart);
        break;
      }
      // EEnum
      case 'gt': {
        let enumName = '';

        if (this.checkNextTokenType('string')) {
          const nextToken = this.nextToken();
          if (nextToken.type === 'string') enumName = nextToken.val;
        } else {
          while (
            this.checkNextTokenType('hole') ||
            this.checkNextTokenType('identifier')
          ) {
            const nextToken = this.nextToken();
            if (nextToken.type === 'hole') {
              enumName += '-';
            } else if (nextToken.type === 'identifier') {
              enumName += nextToken.val;
            }
          }
        }

        this.pushExpr(EEnum(enumName), tokensStart);
        break;
      }
      // EBoolean
      case 'true': {
        this.pushExpr(EBoolean(true), tokensStart);
        break;
      }
      case 'false': {
        this.pushExpr(EBoolean(false), tokensStart);
        break;
      }
      // ENumber
      case 'number': {
        const {val} = this.token;
        try {
          const parsedNumber = JSON.parse(val);
          if (
            typeof parsedNumber !== 'number' ||
            Number.isNaN(parsedNumber) ||
            !Number.isFinite(parsedNumber)
          ) {
            throw new Error();
          }
          this.pushExpr(ENumber(parsedNumber), tokensStart);
        } catch (e: unknown) {
          throw new WFDLSyntaxError(
            makeInvalidNumberFormatError(val),
            this.input,
            this.token.loc
          );
        }
        break;
      }
      // ERecord
      case 'left-curly': {
        this.nextToken();
        const fields = this.createObject();
        this.pushExpr(ERecord(fields), tokensStart);
        break;
      }
      case 'left-bracket': {
        const prevExpr = this.popExpr();
        if (prevExpr == null) {
          // EList
          this.nextToken();
          const items: Array<Expression> = [];
          let traversing = true;
          while (traversing) {
            const currentToken = this.currentToken();
            if (currentToken.type === 'right-bracket') {
              traversing = false;
              continue;
            }
            this.go();
            items.push(this.popExprGuarantee());
            const nextToken2 = this.nextToken();
            // Check if we have a next token, indicating a possibility for more fields
            if (nextToken2.type === 'next') {
              this.nextToken();
              continue;
              // Check if we are at the end of the list
            } else if (nextToken2.type === 'right-bracket') {
              traversing = false;
              continue;
            }
            throw new WFDLSyntaxError(
              `Unexpected token type: ${nextToken2.type}, expected: 'next' or 'right-bracket'`,
              this.input,
              nextToken2.loc
            );
          }
          this.pushExpr(EList(items), tokensStart);
        } else {
          // ESelect
          const nextToken = this.nextToken();

          if (nextToken.type !== 'string') {
            throw new WFDLSyntaxError(
              `Unexpected token type: ${nextToken.type}, expected: string`,
              this.input,
              nextToken.loc
            );
          }

          this.pushExpr(
            ESelect({
              from: prevExpr,
              prop: nextToken.val,
            }),
            tokensStart
          );

          this.nextToken();
          this.confirmRequiredTokenType('right-bracket');
        }

        break;
      }
      // ECase
      case 'case': {
        this.nextToken();
        this.go();
        const value = this.popExprGuarantee();
        this.nextToken();
        this.confirmRequiredTokenType('left-curly');
        this.nextToken();
        const cases = this.createObject();
        this.pushExpr(
          ECase({
            value,
            cases,
          }),
          tokensStart
        );
        break;
      }
      // EElement
      case 'lt': {
        this.nextToken();

        const openTagType = this.getName(Component.name);

        const currentToken = this.currentToken();
        if (currentToken.type !== 'string') {
          throw new WFDLSyntaxError(
            `Unexpected token type: ${currentToken.type}, expected: string`,
            this.input,
            currentToken.loc
          );
        }
        const id = currentToken.val;

        this.nextToken();

        // Confirm we have a closing >
        this.confirmRequiredTokenType('gt');
        this.nextToken();

        this.go();
        const data = this.popExprGuarantee();
        this.nextToken();
        // Confirm we have an end token
        this.confirmRequiredTokenType('lt-end');
        const nextToken2 = this.nextToken();

        // Store start/end within the source input in order to highlight the
        // entire closing tag if there is a mismatch:
        const start = nextToken2.loc.start;
        const endTagType = this.getName(Component.name);
        const currentToken2 = this.currentToken();
        const end = currentToken2.loc.start;

        // Equality check
        if (!namesEqual(openTagType, endTagType)) {
          throw new WFDLSyntaxError(
            `Expected matching tags for Element definition but got <${toHumanReadableString(
              openTagType
            )}></${toHumanReadableString(endTagType)}>`,
            this.input,
            {start, length: end - start}
          );
        }

        // Confirm we have a closing >
        this.confirmRequiredTokenType('gt');

        this.pushExpr(EElement({id, type: openTagType, data}), tokensStart);
        break;
      }
      // ELiteral
      case 'literal': {
        this.nextToken();
        const name = this.getName(typeName);
        this.confirmRequiredTokenType('left-paren');
        const nextToken = this.nextToken();

        if (nextToken.type !== 'json') {
          throw new WFDLSyntaxError(
            `Unexpected token type: ${nextToken.type}, expected: json`,
            this.input,
            nextToken.loc
          );
        }
        try {
          const value = JSON.parse(nextToken.val);
          this.nextToken();
          this.confirmRequiredTokenType('right-paren');
          this.pushExpr(ELiteral({name, value}), tokensStart);
        } catch (e: unknown) {
          const currentToken = this.currentToken();
          const errorMessage = e instanceof Error ? e.message : String(e);
          throw new WFDLSyntaxError(
            `Failed to parse JSON token ${currentToken.type}: ${errorMessage}`,
            this.input,
            currentToken.loc
          );
        }
        break;
      }
      // EDistinct
      case 'distinct': {
        this.nextToken();
        const name = this.getName(typeName);
        this.confirmRequiredTokenType('left-paren');

        this.go();
        const distinctValueExpr = this.popExpr();
        if (distinctValueExpr === undefined) {
          throw new WFDLSyntaxError(
            'Encountered a distinct without a value expr',
            this.input,
            this.token.loc
          );
        }

        this.confirmRequiredTokenType('right-paren');
        this.pushExpr(
          EDistinct({
            name,
            value: distinctValueExpr,
          }),
          tokensStart
        );

        break;
      }
      // ESelect
      case 'accessor': {
        const nextToken = this.nextToken();
        const prevExpr = this.popExpr();
        if (nextToken.type === 'number' && prevExpr === undefined) {
          const {start, length} = nextToken.loc;
          // Handle e.g. `.1`
          throw new WFDLSyntaxError(
            makeInvalidNumberFormatError('.' + nextToken.val),
            this.input,
            // Error needs to include the previous `.` as well
            {start: start - 1, length: length + 1}
          );
        }
        // Confirm we have an identifier
        if (nextToken.type !== 'identifier') {
          throw new WFDLSyntaxError(
            `Unexpected token type: ${nextToken.type}, expected: identifier`,
            this.input,
            nextToken.loc
          );
        }
        // Check for prevExpr
        if (prevExpr === undefined) {
          throw new WFDLSyntaxError(
            'Encountered accessor (".") without a valid expression to select from',
            this.input,
            nextToken.loc
          );
        }
        const prop = nextToken.val;
        this.pushExpr(ESelect({from: prevExpr, prop}), tokensStart);
        break;
      }
      case 'qualifier': {
        this.nextToken();
        const identifier_ = this.getIdentifierString();

        // Note: We want to merge the previously-parsed variable expression into
        // a qualified variable by treating the previous variable's name as the
        // plugin ID of our qualified variable.
        const previousExpr = this.popExpr();
        if (!previousExpr) {
          this.pushExpr(
            EVariable(Variable.unqualified(identifier(identifier_))),
            tokensStart
          );
        } else {
          const maybeVariable = Expressions.getVariable(previousExpr);
          if (maybeVariable == null) {
            throw new WFDLSyntaxError(
              `Encountered qualifier ("::") without a valid plugin prefix`,
              this.input,
              this.token.loc
            );
          }
          const pluginID = Variable.getName(maybeVariable);
          this.pushExpr(
            Expressions.qualifiedVariable(pluginID, identifier_),
            tokensStart - 1
          );
        }
        break;
      }
      case 'left-paren': {
        // Skip the open paren
        this.nextToken();

        // Function of the call is the last expression on the stack
        const exprBeforeParens = this.popExpr();
        this.go();

        // Confirm we have a closing parenthesis next:
        this.nextToken();
        if (!this.checkTokenType('right-paren')) {
          throw new WFDLSyntaxError(
            'No matching closing parenthesis found',
            this.input,
            this.token.loc
          );
        }

        // If we already had an expression before we encountered this opening
        // parenthesis, then we are calling the result of that expression.
        //
        // e.g.
        //
        // 1. Simple function call:
        //
        // foo(...)
        // ^^^ ^^^
        // fun arg
        //
        // 2. IIFE-style call:
        //
        // (x => x)(...)
        //  ^^^^^^  ^^^
        //  fun     arg
        //
        // 3. Complex IIFE-style call:
        //
        // (case x { ... })(...)
        //  ^^^^^^^^^^^^^^  ^^^
        //  fun             arg
        if (exprBeforeParens != null) {
          const exprInsideParens = this.popExpr();
          if (exprInsideParens == null) {
            throw new WFDLSyntaxError(
              'Expected expression inside parenthesis',
              this.input,
              this.token.loc
            );
          }
          this.pushExpr(
            ECall({
              fun: exprBeforeParens,
              arg: exprInsideParens,
            }),
            tokensStart
          );
        }
        break;
      }
      // identifiers
      case 'identifier': {
        this.pushExpr(
          EVariable(Variable.unqualified(identifier(this.token.val))),
          tokensStart
        );
        break;
      }
      case 'function': {
        const paramVariableExpr = this.popExpr();
        if (paramVariableExpr == null) {
          throw new WFDLSyntaxError(
            'Encountered function ("=>") without a parameter',
            this.input,
            this.token.loc
          );
        }

        const paramVariable = Expressions.getVariable(paramVariableExpr);
        if (paramVariable == null) {
          throw new WFDLSyntaxError(
            'Encountered function ("=>") with an unexpected parameter type',
            this.input,
            this.token.loc
          );
        }
        // Current token is the param of the function
        const param = Variable.getName(paramVariable);
        // Move caret one step, past the param identifier and the "function" token
        this.nextToken();
        this.go();
        const bodyExpr = this.popExpr();
        if (bodyExpr === undefined) {
          throw new WFDLSyntaxError(
            'Encountered function ("=>") without a body',
            this.input,
            this.token.loc
          );
        }
        const fn = EFunction({
          param,
          body: bodyExpr,
        });
        this.pushExpr(fn, tokensStart);
        break;
      }
      case 'union': {
        const tagVariableExpr = this.popExpr();
        if (tagVariableExpr == null) {
          throw new WFDLSyntaxError(
            'Encountered union without a tag',
            this.input,
            this.token.loc
          );
        }

        const tagVariable = Expressions.getVariable(tagVariableExpr);
        if (tagVariable == null) {
          throw new WFDLSyntaxError(
            'Encountered union with an unexpected tag type',
            this.input,
            this.token.loc
          );
        }
        // Current token is the param of the function
        const tag = Variable.getName(tagVariable);
        // Move caret one steps, past the tag identifier and the "union" token
        this.nextToken();
        this.go();
        const valueExpr = this.popExpr();
        if (valueExpr === undefined) {
          throw new WFDLSyntaxError(
            'Encountered union without a value',
            this.input,
            this.token.loc
          );
        }
        this.pushExpr(
          EUnion({
            tag,
            value: valueExpr,
          }),
          tokensStart
        );
        break;
      }

      default: {
        throw new WFDLSyntaxError(
          `Unexpected token: ${JSON.stringify(this.token, null, 2)}`,
          this.input,
          this.token.loc
        );
      }
    }

    // The only "combiner" tokens allowed are '.' and '(',
    // if we found either we want to continue the walk
    if (
      !this.checkNextTokenType('left-paren') &&
      !this.checkNextTokenType('left-bracket') &&
      !this.checkNextTokenType('accessor') &&
      !this.checkNextTokenType('qualifier') &&
      !this.checkNextTokenType('function') &&
      !this.checkNextTokenType('union')
    ) {
      return;
    }
    this.nextToken();
    this.go();
  };
}

type Token =
  | {
      loc: SourceLocation;
      type: 'string';
      val: string;
    }
  | {
      loc: SourceLocation;
      type: 'json';
      val: string;
    }
  | {
      loc: SourceLocation;
      type: 'number';
      val: string;
    }
  | {
      loc: SourceLocation;
      type: 'identifier';
      val: string;
    }
  | {
      loc: SourceLocation;
      type: 'left-curly';
    }
  | {
      loc: SourceLocation;
      type: 'right-curly';
    }
  | {
      loc: SourceLocation;
      type: 'left-paren';
    }
  | {
      loc: SourceLocation;
      type: 'right-paren';
    }
  | {
      loc: SourceLocation;
      type: 'left-bracket';
    }
  | {
      loc: SourceLocation;
      type: 'right-bracket';
    }
  | {
      loc: SourceLocation;
      type: 'true';
    }
  | {
      loc: SourceLocation;
      type: 'false';
    }
  | {
      loc: SourceLocation;
      type: 'literal';
    }
  | {
      loc: SourceLocation;
      type: 'distinct';
    }
  | {
      loc: SourceLocation;
      type: 'case';
    } // -
  | {
      loc: SourceLocation;
      type: 'hole';
    } // >
  | {
      loc: SourceLocation;
      type: 'gt';
    } // <
  | {
      loc: SourceLocation;
      type: 'lt';
    } // </
  | {
      loc: SourceLocation;
      type: 'lt-end';
    } // ^
  | {
      loc: SourceLocation;
      type: 'union';
    } // .
  | {
      loc: SourceLocation;
      type: 'accessor';
    } // ::
  | {
      loc: SourceLocation;
      type: 'qualifier';
    } // =>
  | {
      loc: SourceLocation;
      type: 'function';
    } // ,
  | {
      loc: SourceLocation;
      type: 'next';
    }
  | {
      loc: SourceLocation;
      type: 'pair';
    }; // :

type HumanReadableSourceLocation = {
  endColumn: number;
  endLineNumber: number;
  startColumn: number;
  startLineNumber: number;
};

/**
 * A location within source code.
 */
type SourceLocation = {
  /** The starting byte address */
  start: number;
  /** The length of the region in the source code */
  length: number;
};

/**
 * Based loosely on [`SyntaxError`][1]
 *
 * [1] https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/SyntaxError
 */
export class WFDLSyntaxError extends Error {
  /** The original source code */
  source: string;

  /** The location within `this.source` */
  location: SourceLocation;

  constructor(message: string, source: string, location: SourceLocation) {
    super(message);
    this.source = source;
    this.location = location;
  }

  /**
   * Compute the line and column locations that this error spans.
   *
   * Useful for human-readable error messages and tooling interop.
   */
  getHumanReadableSourceLocation(): HumanReadableSourceLocation {
    return getHumanReadableSourceLocation(this.source, this.location);
  }
}

/**
 * Compute the line and column locations that this error spans.
 *
 * Useful for human-readable error messages and tooling interop.
 */
export function getHumanReadableSourceLocation(
  source: string,
  {start, length}: SourceLocation,
  line: number = 1,
  column: number = 1
): HumanReadableSourceLocation {
  let startLineNumber;
  let startColumn;

  let endLineNumber;
  let endColumn;

  for (let i = 0; i < source.length; ++i) {
    if (i === start) {
      startLineNumber = line;
      startColumn = column;
    }

    if (i >= start + length) {
      endLineNumber = line;
      endColumn = column;
      break;
    }

    const ch = source[i];
    if (ch === '\n') {
      ++line;
      column = 0;
    }

    ++column;
  }

  // If we were out of range for some reason, set everything to the end.
  if (startLineNumber == null) {
    startLineNumber = line;
  }
  if (startColumn == null) {
    startColumn = column;
  }
  if (endLineNumber == null) {
    endLineNumber = line;
  }
  if (endColumn == null) {
    endColumn = column;
  }

  return {
    startLineNumber,
    startColumn,
    endLineNumber,
    endColumn,
  };
}

const VALID_NUMBERS_MSG = [
  'Examples of valid numbers:',
  '',
  '- "10"',
  '- "0.0"',
  '- "0.1"',
  '- "0.12"',
  '- "0.120"',
  '',
  'Examples of invalid numbers:',
  '',
  '- "0xabcd"  (Hexadecimal not supported)',
  '- "0b0101"  (Binary not supported)',
  '- ".01"     (Leading zero is required)',
  '- "0...10"  (Number ranges are not supported)',
].join('\n');

const makeInvalidNumberFormatError = (numStr: string): string =>
  [`Invalid number format: "${numStr}"`, '', VALID_NUMBERS_MSG].join('\n');
